import { type ReactElement, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { ROUTES } from '@/constants/routes';
import {
  invalidateBilling,
  useBillingSummaryUntilChanged,
} from '@/hooks/data/use-billing';

const searchSchema = z.object({
  status: z.enum(['success', 'cancel']),
  kind: z.enum(['checkout', 'topup', 'portal']),
});

export const Route = createFileRoute('/_authenticated/billing/return')({
  validateSearch: searchSchema,
  staticData: { hideAppShell: true, title: 'route.billing_return.title' },
  component: BillingReturnPage,
});

function BillingReturnPage(): ReactElement {
  const { t } = useTranslation('billing');
  const { status, kind } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (status !== 'cancel') return;
    toast.info(t('billing_return.no_changes'));
    void navigate({ to: ROUTES.SETTINGS_BILLING });
  }, [status, navigate, t]);

  // The portal manages cards and invoices, not the plan or credit balance it would poll for —
  // waiting out the full poll budget on every "manage billing" round trip would be a needless
  // spinner for the common case where nothing billing-relevant changed.
  const awaitingChange = status === 'success' && kind !== 'portal';
  const { settled } = useBillingSummaryUntilChanged(awaitingChange);
  const ready = status === 'success' && (kind === 'portal' || settled);

  useEffect(() => {
    if (!ready) return;
    // Only now: a mounted observer refetches on invalidation, and before the webhook it gets the old plan.
    void invalidateBilling(queryClient);
    toast.success(t('billing_return.up_to_date'));
    void navigate({ to: ROUTES.SETTINGS_BILLING });
  }, [ready, navigate, t, queryClient]);

  return (
    <div className="bg-bg-page flex min-h-svh items-center justify-center p-6">
      <div className="text-fg-3 text-fs-13 flex items-center gap-2">
        <LoaderCircle className="size-4 animate-spin" />
        {t('billing_return.finishing_up')}
      </div>
    </div>
  );
}
